import { createSlice } from "@reduxjs/toolkit";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useLazyGetFeaturePropertyQuery } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import HCheckArray from "@rf-helper/check-have-value";
import { useEffect } from "react";
import featureProjectAdapter from "@/src-refactor/modules/private/realestate/adapters/feature-project.adapter";
import featureAdapter from "@/src-refactor/modules/private/realestate/adapters/feature.adapter";
import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";
import usePropertyHelpTypeLocations from "../../hooks/helps/usePropertyHelpTypeLocations";
import { CPropertyFormKeys } from "../../util/schemas/property-schema/propertySchema";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";

interface IFeature {
  closet: I_GENERIC_ITEMS[];
  condominiumData: I_GENERIC_ITEMS[];
  cabinets: I_GENERIC_ITEMS[];
  frames: I_GENERIC_ITEMS[];
  improvements: I_GENERIC_ITEMS[];
  floors: I_GENERIC_ITEMS[];
  doors: I_GENERIC_ITEMS[];
  roof: I_GENERIC_ITEMS[];
  location: I_GENERIC_ITEMS[];
  usage: I_GENERIC_ITEMS[];
  windows: I_GENERIC_ITEMS[];
  houseType: I_GENERIC_ITEMS[];
  roofForm: I_GENERIC_ITEMS[];
  amenities?: I_GENERIC_ITEMS[]; // Optional amenities property
}

interface IInitialState {
  forProperty: IFeature;
  forProject: {
    project: IFeature;
    unit: IFeature;
  };
}

const initialState: IInitialState = {
  forProperty: {
    closet: [],
    condominiumData: [],
    cabinets: [],
    frames: [],
    improvements: [],
    floors: [],
    doors: [],
    roof: [],
    location: [],
    usage: [],
    windows: [],
    houseType: [],
    roofForm: []
  },
  forProject: {
    project: {
      closet: [],
      condominiumData: [],
      cabinets: [],
      frames: [],
      improvements: [],
      floors: [],
      doors: [],
      roof: [],
      location: [],
      usage: [],
      windows: [],
      houseType: [],
      roofForm: [],
      amenities: [] // Added amenities property
    },
    unit: {
      closet: [],
      condominiumData: [],
      cabinets: [],
      frames: [],
      improvements: [],
      floors: [],
      doors: [],
      roof: [],
      location: [],
      usage: [],
      windows: [],
      houseType: [],
      roofForm: [],
      amenities: [] // Added amenities property
    }
  }
};

const propertyFormFeaturesSlice = createSlice({
  name: "propertyFormFeaturesSlice",
  initialState,
  reducers: {
    setNewStateProject: (state, action) => {
      state.forProject = action.payload;
    },
    setNewStateProperty: (state, action) => {
      state.forProperty = action.payload;
    },
    restoreState: () => initialState,
    resetFormFeaturesState: () => initialState // Add reset reducer
  },
  extraReducers: builder => {
    // Define your extra reducers here
  }
});

export const {
  setNewStateProject,
  setNewStateProperty,
  restoreState,
  resetFormFeaturesState
} = propertyFormFeaturesSlice.actions; // Export the reset action

export default propertyFormFeaturesSlice;

/**
 *  Fetch the features for the property form
 * @returns
 */
export const usePropertyFormFetchFeature = () => {
  const dispatch = useDispatch();
  const { control } = useFormContext();
  const { isProject } = usePropertyHelpTypeLocations();
  const [GET_FEATURE] = useLazyGetFeaturePropertyQuery({});
  const PROPERTY_TYPE = useWatch({
    name: CPropertyFormKeys.type_property,
    control
  });

  useEffect(() => {
    if (HCheckArray(PROPERTY_TYPE)) {
      GET_FEATURE({
        params: {
          id: isProject ? 14 : PROPERTY_TYPE[0]?.id || 1
        },
        query: {
          projecttype: isProject ? PROPERTY_TYPE[0]?.id : undefined
        }
      })
        .unwrap()
        .then(res => {
          if (isProject) {
            dispatch(setNewStateProject(featureProjectAdapter(res)));
          } else {
            dispatch(setNewStateProperty(featureAdapter(res)));
          }
        })
        .catch(e => HToastCatchServerError(e));
    }
  }, [PROPERTY_TYPE, GET_FEATURE, dispatch, isProject]);

  return null;
};

export const usePropertyFormFeaturesState = () => {
  const { isProject } = usePropertyHelpTypeLocations();
  const state = useSelector(
    (state: any) => state.propertyFormFeaturesSlice
  ) as IInitialState;
  return {
    forProject: state.forProject,
    forProperty: state.forProperty,
    feature: isProject ? state.forProject.unit : state.forProperty
  };
};

export const useResetFormFeaturesState = () => {
  const dispatch = useDispatch();
  return () => dispatch(resetFormFeaturesState());
};
